<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {DateTime} from 'luxon';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';

interface DateRange {
  from: Date;
  to: Date;
}

interface Props {
  modelValue: DateRange;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: DateRange];
}>();

// Preset options
type PresetOption = 'thisWeek' | 'prevWeek' | '14d' | '30d' | '90d' | 'custom';

const selectedPreset = ref<PresetOption>('thisWeek');
const customFromDate = ref<Date>();
const customToDate = ref<Date>();

// Preset definitions
const presets = [
  { key: 'thisWeek' as const, label: 'This week', type: 'week' as const },
  { key: 'prevWeek' as const, label: 'Previous week', type: 'week' as const },
  { key: '14d' as const, label: 'Last 14d', days: 14 as const },
  { key: '30d' as const, label: 'Last 30d', days: 30 as const },
  { key: '90d' as const, label: 'Last 90d', days: 90 as const }
];

// Computed date range based on selection
const dateRange = computed<DateRange>({
  get() {
    return props.modelValue;
  },
  set(value: DateRange) {
    emit('update:modelValue', value);
  }
});

// Calculate date range for preset
function calculatePresetRange(days: number): DateRange {
  const today = DateTime.local().startOf('day');
  const from = today.minus({ days: days - 1 });
  
  return {
    from: from.toJSDate(),
    to: today.toJSDate()
  };
}

// Calculate week range
function calculateWeekRange(weeksBack: number = 0): DateRange {
  const now = DateTime.local();
  const startOfWeek = now.startOf('week').minus({ weeks: weeksBack });
  const endOfWeek = startOfWeek.endOf('week');
  
  return {
    from: startOfWeek.toJSDate(),
    to: endOfWeek.toJSDate()
  };
}

// Handle preset selection
function selectPreset(preset: PresetOption) {
  selectedPreset.value = preset;
  
  if (preset === 'custom') {
    // Don't change dates when switching to custom
    customFromDate.value = dateRange.value.from;
    customToDate.value = dateRange.value.to;
    return;
  }
  
  if (preset === 'thisWeek') {
    dateRange.value = calculateWeekRange(0);
    return;
  }
  
  if (preset === 'prevWeek') {
    dateRange.value = calculateWeekRange(1);
    return;
  }
  
  const presetConfig = presets.find(p => p.key === preset);
  if (presetConfig && 'days' in presetConfig && presetConfig.days) {
    dateRange.value = calculatePresetRange(presetConfig.days);
  }
}

// Handle custom date application
function applyCustomDates() {
  if (!customFromDate.value || !customToDate.value) {
    return; // No dates selected, do nothing
  }
  
  // Validate dates
  const from = DateTime.fromJSDate(customFromDate.value);
  const to = DateTime.fromJSDate(customToDate.value);
  
  if (from > to) {
    // Swap if from > to
    dateRange.value = {
      from: to.toJSDate(),
      to: from.toJSDate()
    };
  } else {
    // Check max span (120 days)
    const diffDays = to.diff(from, 'days').days;
    if (diffDays > 120) {
      // Truncate to 120 days
      dateRange.value = {
        from: from.toJSDate(),
        to: from.plus({ days: 120 }).toJSDate()
      };
    } else {
      dateRange.value = {
        from: from.toJSDate(),
        to: to.toJSDate()
      };
    }
  }
}

// Validation for custom dates
const customDatesValid = computed(() => {
  if (!customFromDate.value || !customToDate.value) return false;
  
  const from = DateTime.fromJSDate(customFromDate.value);
  const to = DateTime.fromJSDate(customToDate.value);
  const diffDays = Math.abs(to.diff(from, 'days').days);
  
  return diffDays <= 120;
});

const customDateError = computed(() => {
  if (!customFromDate.value || !customToDate.value) return '';
  
  const from = DateTime.fromJSDate(customFromDate.value);
  const to = DateTime.fromJSDate(customToDate.value);
  const diffDays = Math.abs(to.diff(from, 'days').days);
  
  if (diffDays > 120) {
    return 'Date range cannot exceed 120 days';
  }
  
  return '';
});

// Initialize with default preset
selectPreset('thisWeek');

// Watch for external changes to detect if we should update preset selection
watch(() => props.modelValue, (newValue) => {
  // Check if current range matches any preset
  const from = DateTime.fromJSDate(newValue.from);
  const to = DateTime.fromJSDate(newValue.to);
  
  // Check for week-based presets
  const thisWeekRange = calculateWeekRange(0);
  const prevWeekRange = calculateWeekRange(1);
  
  if (from.equals(DateTime.fromJSDate(thisWeekRange.from)) && to.equals(DateTime.fromJSDate(thisWeekRange.to))) {
    selectedPreset.value = 'thisWeek';
    return;
  }
  
  if (from.equals(DateTime.fromJSDate(prevWeekRange.from)) && to.equals(DateTime.fromJSDate(prevWeekRange.to))) {
    selectedPreset.value = 'prevWeek';
    return;
  }
  
  // Check for day-based presets (only if "to" is today)
  const today = DateTime.local().startOf('day');
  if (to.equals(today)) {
    const diffDays = today.diff(from, 'days').days + 1;
    const matchingPreset = presets.find(p => 'days' in p && p.days === diffDays);
    
    if (matchingPreset) {
      selectedPreset.value = matchingPreset.key;
      return;
    }
  }
  
  // Default to custom if no preset matches
  selectedPreset.value = 'custom';
}, { deep: true });
</script>

<template>
  <div class="date-range-controls">
    <div class="flex flex-wrap gap-2 items-center">
      <!-- Preset Buttons -->
      <div class="flex gap-1">
        <Button
          v-for="preset in presets"
          :key="preset.key"
          :label="preset.label"
          :severity="selectedPreset === preset.key ? 'primary' : 'secondary'"
          :outlined="selectedPreset !== preset.key"
          size="small"
          @click="selectPreset(preset.key)"
        />
        <Button
          label="Custom"
          :severity="selectedPreset === 'custom' ? 'primary' : 'secondary'"
          :outlined="selectedPreset !== 'custom'"
          size="small"
          @click="selectPreset('custom')"
        />
      </div>
      
      <!-- Custom Date Pickers -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 transform -translate-y-1"
        enter-to-class="opacity-100 transform translate-y-0"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 transform translate-y-0"
        leave-to-class="opacity-0 transform -translate-y-1"
      >
        <div v-if="selectedPreset === 'custom'" class="flex gap-2 items-center">
          <DatePicker
            v-model="customFromDate"
            placeholder="From"
            dateFormat="yy-mm-dd"
            :maxDate="customToDate"
            class="w-32"
          />
          <span class="text-muted-color">to</span>
          <DatePicker
            v-model="customToDate"
            placeholder="To"
            dateFormat="yy-mm-dd"
            :minDate="customFromDate"
            :maxDate="new Date()"
            class="w-32"
          />
          <Button
            label="Apply"
            size="small"
            :disabled="!customDatesValid"
            @click="applyCustomDates"
          />
        </div>
      </Transition>
    </div>
    
    <!-- Custom Date Error -->
    <div v-if="customDateError" class="text-red-600 text-xs mt-1">
      {{ customDateError }}
    </div>
  </div>
</template>

<style scoped>
.date-range-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
